/**
 * Brill design tokens — two scopes, not a conventional light/dark mode.
 *
 *   CHROME  — the sidebar, main panel header, locked-panel header.
 *             Fixed indigo in both panel modes. Never themed.
 *   CANVAS  — the center panel, composer, locked-output body.
 *             This is the only thing the "Light panel / Dark panel" toggle swaps.
 *
 * Consume these as CSS custom properties (`var(--chrome-bg)`, `var(--bg-primary)`),
 * not by importing the objects — that is what lets the canvas swap at runtime.
 * The objects below exist to emit the properties and for the rare JS-side need
 * (e.g. an SVG stroke that cannot take a var).
 */

/**
 * The app UI typeface. Lexend was designed to reduce reading friction, which
 * is why it is here rather than a neutral grotesque — the audience reads a lot
 * of generated prose. Self-hosted as a variable font; see public/index.html.
 *
 * Poppins is the marketing face and is deliberately absent from the product.
 * The fallbacks are ordered so a font failure degrades to the platform UI face
 * rather than to Times.
 */
export const FONT_STACK =
  "'Lexend', 'Segoe UI', system-ui, -apple-system, sans-serif";

/* Raw brand palette. Identity values, emitted as `--brand-*` on :root and never
   themed — a logo gradient or the Brill avatar means the same thing in both panel
   modes. Prefer a semantic token so the meaning travels with the color; reach for
   `--brand-*` only when the value genuinely IS the identity. */
export const BRAND = {
  teal: "#007D6E",        // primary / logo
  tealLight: "#0AEFD3",   // accent, best contrast on dark
  tealMid: "#14B8A6",     // contrast-tuned mid teal for labels on dark
  tealDark: "#214042",    // depth
  salmon: "#FF5656",      // destructive / urgency, and the small "you" mark
  indigo: "#27233A",      // chrome base
  indigoDeep: "#000321",  // canvas base (dark)
  amber: "#F59E0B",       // accepted accent, off-brand but kept
  peach: "#E8A87C",       // draft status only
};

/* ── Chrome: fixed in both panel modes ─────────────────────────────────── */
export const CHROME = {
  "chrome-bg": "#27233A",
  "chrome-elevated": "#302B47",
  "chrome-quiet": "rgba(255,255,255,0.05)",
  "chrome-hover": "rgba(255,255,255,0.085)",
  "chrome-border": "rgba(255,255,255,0.10)",
  "chrome-border-strong": "rgba(255,255,255,0.20)",
  "chrome-text": "#F0EEEB",
  "chrome-text-secondary": "#A8B0B8",
  "chrome-muted": "#7C818E",
  "chrome-disabled": "#6C7280",
  "chrome-accent": "#0AEFD3",
  "chrome-accent-solid": "#007D6E",
  "chrome-accent-quiet": "rgba(10,239,211,0.14)",
  "chrome-accent-line": "rgba(10,239,211,0.34)",
  "chrome-on-accent": "#27233A",          // on the bright #0AEFD3 fill
  "chrome-on-accent-solid": "#FFFFFF",    // on the deep #007D6E fill — 4.4:1
  "chrome-danger": "#FF5656",             // destructive actions in chrome (Reset module)
  "chrome-danger-line": "rgba(255,86,86,0.27)",
};

/* ── Canvas: dark ──────────────────────────────────────────────────────────
   DERIVED, not faithful — unlike the light scope below, there is no existing
   dark Accelerator canvas to promote. Only these five come from shipped code
   (SocialGenerator.jsx:9 + MomentumHub): #000321, #13151c, #1A1D24, #252830,
   #F0EDE8. Everything else is a proposal and is fair game to change.

   Rebased on your #000321 / #13151c rather than the handoff's indigo
   (#1B1828 / #221E33) so nothing already live has to be retuned. Because the
   base is near-black rather than indigo, raised surfaces are translucent
   WHITE — the handoff used translucent black, which does nothing over #000321.

   Nothing consumes this yet.                                                */
export const CANVAS_DARK = {
  "bg-primary": "#000321",
  "bg-surface": "#13151c",
  "bg-elevated": "#1A1D24",
  "bg-sunken": "#00020F",
  "surface-grey": "rgba(255,255,255,0.045)",
  "input-bg": "#0B0D15",
  "bg-hover": "rgba(255,255,255,0.055)",
  "bg-quiet": "rgba(255,255,255,0.035)",
  "text-primary": "#F0EDE8",
  "text-secondary": "#A8B0B8",
  "text-muted": "#6B7280",
  "text-disabled": "#5E646E",
  "border-subtle": "#252830",
  "border-strong": "#3A3F4A",
  "border-hairline": "#1E212A",  // parity with the light scope — every token
  "border-panel": "#171A22",     // must exist in both or dark inherits light
  "accent": "#0AEFD3",
  "accent-deep": "#007D6E",
  "accent-quiet": "rgba(10,239,211,0.12)",
  "accent-hover": "rgba(10,239,211,0.20)",
  "accent-line": "rgba(10,239,211,0.34)",
  "focus-ring": "rgba(10,239,211,0.22)",
  "btn-bg": "#0AEFD3",
  "btn-text": "#27233A",
  "btn-off-bg": "rgba(255,255,255,0.07)",
  "btn-off-text": "#6C7280",
  // Ends at tealMid rather than accent-deep so `btn-text` clears 4.5:1 along the
  // whole ramp; #27233A on #007D6E is only ~3.1:1.
  "btn-grad-from": "#0AEFD3",
  "btn-grad-to": "#14B8A6",
  // Selected pill / segmented control. Light is solid indigo; over near-black
  // that reads as a hole, so dark selects with accent instead.
  "select-bg": "rgba(10,239,211,0.16)",
  "select-text": "#0AEFD3",
  "warn-bg": "rgba(245,200,66,0.12)",
  "warn-line": "rgba(245,200,66,0.38)",
  "warn-text": "#F5C842",
  "status-draft": "#E8A87C",
  "status-danger": "#FF5656",
  // Salmon as *text*. #FF5656 clears 4.5:1 on this canvas, so dark keeps it;
  // the light scope has to darken (see below). Solid fills stay `status-danger`.
  "danger-text": "#FF5656",
  "danger-quiet": "rgba(255,86,86,0.09)",
  "danger-line": "rgba(255,86,86,0.27)",
  // The "you" bubble is brand indigo, not salmon. A salmon FILL that size was
  // jarring on this canvas (her call, 2026-09-18) — salmon survives as the small
  // avatar mark and the Send fill, where it reads as accent, not alarm.
  // #27233A is still the LIGHTEST surface in the dark stack (canvas #000321,
  // Brill bubble #0B0E2B), so the bubble separates without needing a border.
  "you-bg": "#27233A",
  "you-text": "#F0EDE8",
  "you-line": "rgba(255,255,255,0.10)",
  "you-glow": "none",
  // Salmon, kept small: the 28px "you" avatar and the Send button.
  "you-mark": "#FF5656",
  "you-mark-text": "#FFFFFF",
  // Salmon as 10px TEXT under her bubble. Full salmon clears 4.5:1 here, the
  // same reason `danger-text` keeps it on this canvas.
  "you-stamp": "#FF5656",
  // Elevation is carried by lightness on the dark canvas, so the quiet shadow
  // drops out entirely rather than becoming invisible black-on-black.
  "shadow-soft": "none",
  "shadow-bubble": "0 1px 4px rgba(0,0,0,0.45)",
  "shadow-menu": "0 18px 50px rgba(0,0,0,0.66)",
  // The scrollbar's thumb: a see-through LIGHTENING of whatever it sits on,
  // Google's way (her ask, 2026-10-01). Light canvas darkens instead.
  "scroll-thumb": "rgba(255,255,255,0.16)",
  "scroll-thumb-hover": "rgba(255,255,255,0.30)",
  // A thin light outline round the thumb — her Whisper bar's glassy edge.
  // A SECOND-LEVEL MENU (her rule, 2026-10-20): when a menu item opens its own
  // menu — Ask Brill's chats first — that level of the sidebar takes these.
  // On the light canvas it is "cream blush": the warm cream below with 3%
  // salmon over it, after light pink read as too peach. Sidebar.jsx.
  "menu2-bg": "#080523",
  "menu2-text": "#F0EDE8",
  "menu2-text-secondary": "#A8B0B8",
  "menu2-accent": "#0AEFD3",
  "menu2-border": "rgba(255,255,255,0.10)",
  "menu2-quiet": "rgba(255,255,255,0.05)",
  "menu2-hover": "rgba(255,255,255,0.085)",
  "menu2-row-on": "rgba(255,255,255,0.09)",
  // Ask Brill's canvas. Warm on the light side so it sits beside the cream
  // blush menu without the join reading as a clash.
  "ask-bg": "#000321",
  "scroll-thumb-stroke": "rgba(255,255,255,0.42)",
};

/* ── Canvas: light ────────────────────────────────────────────────────────
   FAITHFUL. Every value here is one the app already renders today — this is a
   rename, not a repalette, so adopting these tokens cannot move a pixel.
   Source of each value is noted where it isn't obvious.

   Deliberately NOT the design handoff's light column: that invented a cooler
   grey set (#F6F6F8 bg, #2C2C2C text, black-alpha borders) which drifts off
   brand. If we ever want to consolidate the near-duplicate greys below, that
   is a separate, separately-reviewable change.

   `accent` is #007D6E and not #0AEFD3 — the light teal fails contrast on
   white, so anything reading as accent text or icon on the canvas uses this. */
export const CANVAS_LIGHT = {
  "bg-primary": "#F8F7FA",      // ChatPanel.jsx:337 — the chat background
  "bg-surface": "#FFFFFF",
  "bg-elevated": "#FFFFFF",
  "bg-sunken": "#F0EFF6",
  "surface-grey": "#FFFFFF",    // Brill chat bubbles are white today
  "input-bg": "#FFFFFF",
  "bg-hover": "rgba(39,35,58,0.04)",
  "bg-quiet": "rgba(39,35,58,0.03)",
  "text-primary": "#1A1A2E",    // body text
  "text-secondary": "#555555",
  "text-muted": "#888888",
  "text-disabled": "#AAAAAA",
  "border-subtle": "#E8E8F0",   // dominant border today (6 uses)
  "border-strong": "#E0DFE8",   // button borders (7 uses)
  "border-hairline": "#EBEBF2", // kept so migrating these call sites is a no-op
  "border-panel": "#F0F0F8",    // ditto
  "accent": "#007D6E",
  "accent-deep": "#214042",     // C.tealDark, the gradient partner
  "accent-quiet": "rgba(0,125,110,0.07)",  // === today's `${C.teal}11`
  "accent-hover": "rgba(0,125,110,0.12)",  // === today's `${C.teal}1e`
  "accent-line": "rgba(0,125,110,0.27)",   // === today's `${C.teal}44`
  "focus-ring": "rgba(0,125,110,0.13)",    // === today's `${C.teal}22`
  "btn-bg": "#007D6E",
  "btn-text": "#FFFFFF",
  "btn-off-bg": "#CCCCCC",
  "btn-off-text": "#FFFFFF",
  "btn-grad-from": "#007D6E",   // === today's `${C.teal} -> ${C.tealDark}`
  "btn-grad-to": "#214042",
  "select-bg": "#27233A",       // today's active Ask Brill pill
  "select-text": "#FFFFFF",
  "warn-bg": "#FFF8E1",         // today's save/history notices
  "warn-line": "#F5C842",
  "warn-text": "#7A5C00",
  "status-draft": "#F59E0B",
  "status-danger": "#FF5656",
  // Darkened salmon — #FF5656 is only 3.1:1 on white, so destructive *text*
  // (Delete, a "core" badge) needs its own value. 4.6:1, same hue family.
  "danger-text": "#D93A3A",
  "danger-quiet": "rgba(255,86,86,0.09)",
  "danger-line": "rgba(255,86,86,0.27)",
  // See the dark scope for why this is indigo and not salmon.
  "you-bg": "#27233A",
  "you-text": "#F0EEEB",        // === chrome-text; the ink her mockup uses here
  "you-line": "transparent",    // the fill carries it; no border needed
  "you-glow": "0 2px 8px rgba(39,35,58,0.18)",  // neutral lift, no pink halo
  "you-mark": "#FF5656",
  "you-mark-text": "#FFFFFF",
  // Darkened for the same reason `danger-text` is — #FF5656 is 2.9:1 on this
  // canvas and the stamp is 10px, which is small text by any measure. A shade
  // deeper than `danger-text` because that one only reaches 4.3:1 here; this
  // clears 4.7:1 in the same hue family.
  "you-stamp": "#CF3434",
  "shadow-soft": "0 1px 3px rgba(0,0,0,0.05)",
  "shadow-bubble": "0 1px 4px rgba(0,0,0,0.07)",
  "shadow-menu": "0 18px 50px rgba(39,35,58,0.18)",
  "scroll-thumb": "rgba(0,0,0,0.20)",
  "scroll-thumb-hover": "rgba(0,0,0,0.34)",
  // See the dark scope. 3% salmon over #FAF7F6.
  "menu2-bg": "#FAF2F1",
  "menu2-text": "#1A1A2E",
  "menu2-text-secondary": "#4A4F5C",
  "menu2-accent": "#007D6E",
  "menu2-border": "rgba(39,35,58,0.10)",
  "menu2-quiet": "rgba(255,255,255,0.70)",
  "menu2-hover": "rgba(255,255,255,0.60)",
  "menu2-row-on": "#FFFFFF",
  "ask-bg": "#FAF7F6",
  "scroll-thumb-stroke": "rgba(255,255,255,0.75)",
};

/* Phase tones — the switcher tab fill and its progress bar.
   Each has to clear 4.5:1 against `chrome-on-accent` (#27233A), the ink on a
   selected tab, because that label is 10px bold. Warm / cool / warm, so no two
   neighbouring tabs read as the same family. */
export const PHASE_TONES = {
  // Was #E8A87C. That is BRAND.peach, which this file scopes to draft status
  // and which is not in the brand palette — and being a desaturated amber, it
  // made Ideation look like a washed-out Momentum. Salmon is an actual brand
  // colour and already reads as a category accent elsewhere (Connect).
  ideation: "#FF5656",     // 4.85:1
  accelerator: "#0AEFD3",  // 13.9:1
  momentum: "#F59E0B",     // 8.2:1
};

const declarations = (obj) =>
  Object.entries(obj).map(([k, v]) => `--${k}: ${v};`).join(" ");

/* BRAND is keyed camelCase for JS ergonomics; CSS custom properties are kebab. */
const brandDeclarations = () =>
  Object.entries(BRAND)
    .map(([k, v]) => `--brand-${k.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}: ${v};`)
    .join(" ");

/**
 * CSS for the whole token layer. Chrome and brand sit on :root; the canvas scope
 * is selected by [data-panel] so the toggle is a single attribute flip.
 */
export function tokenCSS() {
  return `
    :root { ${brandDeclarations()} ${declarations(CHROME)} ${declarations(CANVAS_LIGHT)} }
    [data-panel="light"] { ${declarations(CANVAS_LIGHT)} }
    [data-panel="dark"] { ${declarations(CANVAS_DARK)} }
  `;
}

/**
 * Deprecated. The flat palette the app was built on, kept so the ~26 files
 * importing `C` keep working while they migrate to tokens. Do not use in new
 * code — reach for a semantic token instead.
 */
export const C = {
  indigo: "#27233A",
  teal: "#007D6E",
  tealLight: "#0AEFD3",
  tealDark: "#214042",
  salmon: "#FF5656",
};
